import {Dropdown, DropdownItem, Tooltip} from 'platform/components';
import {Hide, HStack, Link, Show, VStack} from 'platform/foundation';

import {isFalse} from 'ramda-adjunct';

import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {useGetVehicleWarehouseManagementSettingsQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {useVehicleWarehouse} from '../../../hooks/useVehicleWarehouse/useVehicleWarehouse';
import {useVehicleWarehouseContext} from '../../../hooks/useVehicleWarehouse/useVehicleWarehouseContext';
import {NoPermissionTooltip} from '../../NoPermissionTooltip/NoPermissionTooltip';

interface VehicleWarehouseProps extends TestIdProps {
  vehicleId: string;
}

export function VehicleWarehouse(props: VehicleWarehouseProps) {
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: props.vehicleId});

  const {refreshDataGrid} = useVehicleWarehouseContext();

  const {
    availableActions,
    openStockInDialog,
    openStockOutDialog,
    openCancelStockInDialog,
    openCancelStockOutDialog,
    openTransferDialog,
  } = useVehicleWarehouse({
    vehicleId: props.vehicleId,
    saleVehicleId: saleVehicle?.id,
    vehicleWarehouseId: saleVehicle?.vehicleWarehouse?.id,
    onMovementComplete: refreshDataGrid,
    'data-testid': props['data-testid'],
  });

  const {data: warehouseManagmentData} = useGetVehicleWarehouseManagementSettingsQuery();

  const hasWarehouseManagementEnabled = warehouseManagmentData?.isEnabled;

  const permissions = usePermissions({
    permissionKeys: [
      'vehicleIssueFromVehicleWarehouse',
      'vehicleReceiveToVehicleWarehouse',
      'vehicleCancelReceivalWarehouse',
      'vehicleCancelIssuingWarehouse',
    ],
  });

  const [
    canIssueFromVehicleWarehouse,
    canReceiveToVehicleWarehouse,
    canCancelReceivalWarehouse,
    canCancelIssuingWarehouse,
  ] = permissions;

  const shouldShowActions = [
    availableActions.issue && canIssueFromVehicleWarehouse,
    availableActions.receive && canReceiveToVehicleWarehouse,
    availableActions.cancelIssue && canCancelIssuingWarehouse,
    availableActions.cancelReceipt && canCancelReceivalWarehouse,
    availableActions.transfer && canReceiveToVehicleWarehouse && canIssueFromVehicleWarehouse,
  ].some(Boolean);

  const isBrokerage = saleVehicle?.type === 'BROKERAGE';

  return (
    <VStack spacing={1}>
      <Show when={shouldShowActions}>
        <Dropdown
          dropdownControl={
            <Tooltip
              label={i18n.t('entity.controls.labels.vehicle_warehouse.tooltip')}
              childrenWrapperDisplay="flex"
            >
              <Link
                size="small"
                leftIcon="custom/warehouse"
                data-testid={suffixTestId('link', props)}
                title={
                  saleVehicle?.vehicleWarehouse?.name ??
                  i18n.t('entity.vehicleWarehouse.labels.noWarehouseAssigned')
                }
              />
            </Tooltip>
          }
          data-testid={suffixTestId('vehicleWarehouse', props)}
        >
          <Show when={availableActions.cancelReceipt && canCancelReceivalWarehouse}>
            <DropdownItem
              label={i18n.t('entity.vehicleWarehouse.actions.warehouseCancelStockIn')}
              onClick={openCancelStockInDialog}
            />
          </Show>
          <Show when={availableActions.cancelIssue && canCancelIssuingWarehouse}>
            <DropdownItem
              label={i18n.t('entity.vehicleWarehouse.actions.warehouseCancelStockOut')}
              onClick={openCancelStockOutDialog}
            />
          </Show>
          <Show
            when={
              availableActions.receive &&
              canReceiveToVehicleWarehouse &&
              hasWarehouseManagementEnabled
            }
          >
            <DropdownItem
              label={i18n.t('entity.vehicleWarehouse.actions.warehouseStockIn')}
              onClick={openStockInDialog}
            />
          </Show>
          <Show when={availableActions.issue && canIssueFromVehicleWarehouse}>
            <DropdownItem
              label={i18n.t('entity.vehicleWarehouse.actions.warehouseStockOut')}
              onClick={() => openStockOutDialog()}
            />
          </Show>
          <Show
            when={
              availableActions.transfer &&
              canIssueFromVehicleWarehouse &&
              canReceiveToVehicleWarehouse
            }
          >
            <DropdownItem
              label={i18n.t('entity.vehicleWarehouse.actions.warehouseTransfer')}
              onClick={() => openTransferDialog()}
            />
          </Show>
        </Dropdown>
      </Show>
      <Hide when={shouldShowActions}>
        <Show when={isBrokerage}>
          <Tooltip
            label={i18n.t('entity.vehicleWarehouse.labels.noAssign')}
            childrenWrapperDisplay="flex"
          >
            <HStack>
              <Link
                isDisabled
                size="small"
                leftIcon="custom/warehouse"
                data-testid={suffixTestId('link', props)}
                title={
                  saleVehicle?.vehicleWarehouse?.name ??
                  i18n.t('entity.vehicleWarehouse.labels.noWarehouseAssigned')
                }
              />
            </HStack>
          </Tooltip>
        </Show>
        <Hide when={isBrokerage}>
          <NoPermissionTooltip shouldShowTooltip={permissions.every(isFalse)}>
            <HStack>
              <Link
                isDisabled
                size="small"
                leftIcon="custom/warehouse"
                data-testid={suffixTestId('link', props)}
                title={
                  saleVehicle?.vehicleWarehouse?.name ??
                  i18n.t('entity.vehicleWarehouse.labels.noWarehouseAssigned')
                }
              />
            </HStack>
          </NoPermissionTooltip>
        </Hide>
      </Hide>
    </VStack>
  );
}
